import { isAddressAutocompleteDebugEnabled } from '../shared/hooks/useAddressAutocomplete';
import { DEFAULT_SITE_DISPLAY_NAME } from "@shared/siteDisplayName.js";
import { getApiBase } from '../shared/utils/urlHelpers';
import { ensureStackWake, isScaleToZeroClient } from '../shared/utils/scaleToZeroWake';
import { getToken, setToken, isLoggedOutLocally } from './client';
import {
  applyDesktopBrowserAuthSessionIsolation,
  shouldIsolateDesktopBrowserAuthSession,
} from '../account/desktopAuthFlow.js';
import { getPrimaryLightTint, getPrimaryLighterTint } from '../shared/utils/colorUtils';
const DEFAULT_PRIMARY = 'var(--nm-blue)';
const DEFAULTS = {
  apiUrl: '/api',
  userLoggedIn: false,
  userRole: 'standard',
  isTrustedCustomer: false,
  userEmail: '',
  userDisplayName: '',
  isAdmin: false,
  adminAccess: false,
  impersonatorId: null,
  adminOrigin: null,
  maintenanceMode: false,
  primaryColor: DEFAULT_PRIMARY,
  primaryHover: 'var(--nm-blue-hover)',
  primaryLightTint: getPrimaryLightTint(DEFAULT_PRIMARY),
  primaryLighterTint: getPrimaryLighterTint(DEFAULT_PRIMARY),
  fontFamily: 'Noto Sans, sans-serif',
  displayName: DEFAULT_SITE_DISPLAY_NAME,
  shipperNumbers: {
    primary: '',
    valid: []
  },
  categoriesConfig: {},
  formOptionsConfig: [],
  hiddenUpsServiceTypes: [],
  showAllRatesAtOnce: false,
  basicMarkup: 0,
  additionalHandlingMarkup: 0,
  pickupMarkup: 0,
  carrierMarkups: {},
  serviceMarkups: {},
  stripePublishableKey: '',
  isStagingSite: false,
  enableWooCommerceStoreConnections: true,
  enableEbayStoreConnections: true,
  enableShopifyStoreConnections: false,
  enableEtsyStoreConnections: false,
  enableAmazonStoreConnections: false,
  enableSquareStoreConnections: false,
  enableLightspeedRStoreConnections: true,
  enableLightspeedXStoreConnections: false,
  enableWalmartStoreConnections: false,
  enableReverbStoreConnections: false,
  enableBrickLinkStoreConnections: false,
  enableEcwidStoreConnections: false,
  enableWixStoreConnections: false,
  enableSquarespaceStoreConnections: false,
  enableMagentoStoreConnections: false,
  /** @deprecated Prefer per-channel enable*StoreConnections flags. */
  enableWooShopifyStoreConnections: false,
  enableMcp: false,
  enableLabelQr: false,
  enableTrackingEmailOptIn: false,
  cloudRunMinInstances: 1,
  scaleToZero: false,
  addressAutocompleteAvailable: '0',
  quotePlacesAutocompleteAvailable: '0',
  quoteRedirectPage: 'get-label',
  recaptchaSiteKey: '',
  googleOAuthClientId: '',
  googleOAuthAvailable: false,
  googleOAuthJavascriptOriginPatterns: [],
  portalConfig: null,
  contactEmail: '',
  emailVerified: true,
  welcomeCouponCode: null,
  welcomeCouponPromo: null,
  welcomeCouponPromoHeadline: null,
  embedQuoteMarkupPercent: 0,
  quoteEmbedPublicId: null,
  isEnterprise: false
};
const CONFIG_FETCH_TIMEOUT_MS = 8000;
export function getConfigDefaults() {
  return {
    ...DEFAULTS
  };
}
function acConfigDebug(payload) {
  try {
    if (typeof window !== 'undefined' && isAddressAutocompleteDebugEnabled()) {
      console.log('[nm-address-ac:config]', performance.now().toFixed(0) + 'ms', payload);
    }
  } catch (_) {}
}
function notifyConfigUpdated(source) {
  try {
    if (typeof window !== 'undefined') {
      const d = window.nitromuleReactData;
      acConfigDebug({
        source: source || 'notify',
        addressAutocompleteAvailable: d?.addressAutocompleteAvailable,
        dispatching: 'nitromule-config'
      });
      window.dispatchEvent(new CustomEvent('nitromule-config'));
    }
  } catch (_) {}
}
export async function loadAppConfig() {
  const base = getApiBase();
  const loggedOutLocally = isLoggedOutLocally();
  if (loggedOutLocally) {
    setToken('');
    // Clear the session cookie before /config so guest catalog is not the previous user's
    // Woo/Shopify products and packages.
    try {
      await fetch(`${base}/nitromule/v1/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (_) {}
  }
  const token = loggedOutLocally ? '' : getToken();
  const headers = token ? {
    Authorization: `Bearer ${token}`
  } : {};
  const fetchWithTimeout = () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CONFIG_FETCH_TIMEOUT_MS);
    return fetch(`${base}/config`, {
      credentials: loggedOutLocally ? 'omit' : 'include',
      headers,
      signal: controller.signal
    }).then(res => {
      clearTimeout(timeoutId);
      return res;
    }).catch(e => {
      clearTimeout(timeoutId);
      throw e;
    });
  };
  try {
    const res = await fetchWithTimeout();
    const data = await res.json();
    const preserveHttpNotFound = typeof window !== 'undefined' && window.nitromuleReactData?.httpNotFound === true;
    const merged = {
      ...DEFAULTS,
      ...data
    };
    const isolateDesktopBrowserAuth = shouldIsolateDesktopBrowserAuthSession();
    if (loggedOutLocally) {
      merged.userLoggedIn = false;
      merged.token = '';
      setToken('');
    } else if (isolateDesktopBrowserAuth) {
      setToken('');
    } else if (data.token) {
      setToken(data.token);
    }
    window.nitromuleReactData = applyDesktopBrowserAuthSessionIsolation(merged);
    if (preserveHttpNotFound) window.nitromuleReactData.httpNotFound = true;
    if (typeof document !== 'undefined') {
      const cur = (document.title || '').trim();
      if (cur === '' || cur === 'nitromule' || cur === 'NitroMule') {
        const portalTitle = (data.portalConfig?.site_title || '').trim();
        const displayName = (data.displayName || '').trim();
        const title = portalTitle || displayName;
        if (title) document.title = title;
      }
    }
    notifyConfigUpdated('loadAppConfig:ok');
    // Scale-to-zero: start warming NitroMule + ABI immediately (before App mounts).
    if (isScaleToZeroClient()) ensureStackWake('site_load');
    return window.nitromuleReactData;
  } catch (e) {
    if (e?.name === 'AbortError') {
      console.error('Config fetch timed out after', CONFIG_FETCH_TIMEOUT_MS / 1000, 's');
    } else {
      console.error('Config fetch failed:', e?.message || e);
    }
    const apiUrl = base + (base.endsWith('/api') ? '' : '/api');
    const preserveHttpNotFound = typeof window !== 'undefined' && window.nitromuleReactData?.httpNotFound === true;
    window.nitromuleReactData = {
      ...DEFAULTS,
      apiUrl
    };
    if (preserveHttpNotFound) window.nitromuleReactData.httpNotFound = true;
    notifyConfigUpdated('loadAppConfig:catch');
    if (isScaleToZeroClient()) ensureStackWake('site_load_config_error');
    return window.nitromuleReactData;
  }
}
